import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import PageHeader from "@/components/common/PageHeader";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { fmtDate } from "@/lib/format";
import { toast } from "sonner";
import { downloadBlob } from "@/lib/download";
import { ShieldCheck, MessageSquareWarning, Download, AlertTriangle } from "lucide-react";

// Iter-200 · Actions we consider "dangerous" — surface these on their own
// tab so auditors can zero in on wipes, bulk deletes, reversals, restores.
const DANGER_ACTIONS = new Set([
  "wipe_all", "bulk_delete", "delete", "soft_delete",
  "schedule_delete_organization", "cancel_pending_delete",
  "restore_organization", "clone_organization",
  "bulk_toggle_active",
]);

const ENTITIES = ["", "party", "voucher", "account", "organization",
                    "user_gstin_access", "tally_import_batch", "zoho_import_batch",
                    "sales_invoice", "purchase_bill", "fixed_asset", "branch"];

export default function AuditPage() {
  const [logs, setLogs] = useState([]);
  const [remarks, setRemarks] = useState([]);
  const [selected, setSelected] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ entity: "voucher", entity_id: "", remark: "", severity: "info" });

  // Iter-200 · filters
  const [f, setF] = useState({ entity: "", action: "", from_ts: "", to_ts: "" });
  const [exportBusy, setExportBusy] = useState(false);

  const load = async () => {
    const params = {};
    if (f.entity) params.entity = f.entity;
    if (f.action) params.action = f.action;
    if (f.from_ts) params.from_ts = new Date(f.from_ts).toISOString();
    if (f.to_ts)   params.to_ts   = new Date(f.to_ts + "T23:59:59").toISOString();
    setLogs((await api.get("/audit-logs", { params })).data);
    setRemarks((await api.get("/audit-remarks")).data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const addRemark = async () => {
    if (!form.remark) return toast.error("Remark required");
    try { await api.post("/audit-remarks", form); toast.success("Remark logged"); setOpen(false); setForm({ ...form, remark: "", entity_id: "" }); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const doExport = async () => {
    setExportBusy(true);
    try {
      const params = {};
      if (f.entity) params.entity = f.entity;
      if (f.action) params.action = f.action;
      if (f.from_ts) params.from_ts = new Date(f.from_ts).toISOString();
      if (f.to_ts)   params.to_ts   = new Date(f.to_ts + "T23:59:59").toISOString();
      await downloadBlob("/audit-logs/export.xlsx", `audit_trail_${new Date().toISOString().slice(0,10)}.xlsx`, params);
      toast.success("Audit trail exported");
    } catch (e) { toast.error(e?.response?.data?.detail || "Export failed"); }
    setExportBusy(false);
  };

  const dangerLogs = useMemo(() => logs.filter((l) => DANGER_ACTIONS.has(l.action)), [logs]);
  const uniqActions = useMemo(() => Array.from(new Set(logs.map((l) => l.action))).sort(), [logs]);

  const renderRow = (l, prefix = "") => (
    <tr key={l.id} onClick={() => setSelected(l)} className="cursor-pointer hover:bg-muted/40" data-testid={`audit-row-${prefix}${l.id}`}>
      <td className="text-xs font-mono whitespace-nowrap">{new Date(l.timestamp).toLocaleString("en-IN")}</td>
      <td className="whitespace-nowrap">{l.user_name}</td>
      <td>
        <span className={`text-[10px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded-sm ${DANGER_ACTIONS.has(l.action) ? "text-red-600 bg-red-50 border border-red-500/40" : "text-accent"}`}>
          {l.action}
        </span>
      </td>
      <td className="text-xs">{l.entity}</td>
      <td className="text-xs font-mono text-muted-foreground truncate max-w-[240px]">
        {l.after?.count ? `${l.after.count} items` :
         l.after?.deleted !== undefined ? `${l.after.deleted} deleted · ${l.after.soft_deleted || 0} soft` :
         l.after?.soft_deleted !== undefined ? `${l.after.soft_deleted} soft-deleted` :
         (l.entity_id || "")}
      </td>
      <td className="text-right"><ShieldCheck className="w-3.5 h-3.5 text-emerald-600 inline" /></td>
    </tr>
  );

  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="audit-page">
      <PageHeader
        eyebrow="Controls"
        title="Audit Workspace"
        description="Immutable audit trail across every finance action — every wipe, bulk-delete, migrator import, and reversal is logged with actor + snapshot. Filter, drill in, or export for auditors."
        actions={
          <div className="flex gap-2">
            <Button variant="outline" onClick={doExport} disabled={exportBusy} className="rounded-sm gap-1.5" data-testid="audit-export-xlsx">
              <Download className="w-3.5 h-3.5" /> {exportBusy ? "…" : "Export Excel"}
            </Button>
            <Button onClick={() => setOpen(true)} className="rounded-sm bg-accent hover:bg-accent/90 text-white gap-2" data-testid="new-remark-btn">
              <MessageSquareWarning className="w-4 h-4" /> Add Remark
            </Button>
          </div>
        }
      />

      {/* Iter-200 · Filter bar */}
      <div className="card-sharp p-3 grid grid-cols-2 md:grid-cols-5 gap-3 items-end" data-testid="audit-filters">
        <div>
          <Label className="text-[10px] font-mono uppercase">Entity</Label>
          <Select value={f.entity || "__all"} onValueChange={(v) => setF({ ...f, entity: v === "__all" ? "" : v })}>
            <SelectTrigger className="rounded-sm h-9 mt-1" data-testid="af-entity"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all">Any entity</SelectItem>
              {ENTITIES.filter((e) => e).map((e) => <SelectItem key={e} value={e}>{e}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[10px] font-mono uppercase">Action</Label>
          <Select value={f.action || "__all"} onValueChange={(v) => setF({ ...f, action: v === "__all" ? "" : v })}>
            <SelectTrigger className="rounded-sm h-9 mt-1" data-testid="af-action"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all">Any action</SelectItem>
              {uniqActions.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[10px] font-mono uppercase">From</Label>
          <Input type="date" value={f.from_ts} onChange={(e) => setF({ ...f, from_ts: e.target.value })} className="h-9 mt-1" data-testid="af-from" />
        </div>
        <div>
          <Label className="text-[10px] font-mono uppercase">To</Label>
          <Input type="date" value={f.to_ts} onChange={(e) => setF({ ...f, to_ts: e.target.value })} className="h-9 mt-1" data-testid="af-to" />
        </div>
        <div className="flex gap-2">
          <Button onClick={load} className="h-9 rounded-sm" data-testid="af-apply">Apply</Button>
          <Button variant="outline" onClick={() => { setF({ entity: "", action: "", from_ts: "", to_ts: "" }); setTimeout(load, 0); }} className="h-9 rounded-sm" data-testid="af-reset">Reset</Button>
        </div>
      </div>

      <Tabs defaultValue="trail">
        <TabsList className="rounded-sm">
          <TabsTrigger value="trail" className="rounded-sm" data-testid="tab-trail">
            Audit Trail <span className="ml-1.5 text-[10px] text-muted-foreground">({logs.length})</span>
          </TabsTrigger>
          <TabsTrigger value="danger" className="rounded-sm text-red-600" data-testid="tab-danger">
            <AlertTriangle className="w-3.5 h-3.5 mr-1" />
            Danger Log <span className="ml-1.5 text-[10px] font-mono">({dangerLogs.length})</span>
          </TabsTrigger>
          <TabsTrigger value="gstin_access" className="rounded-sm" data-testid="tab-gstin-access">GSTIN Access</TabsTrigger>
          <TabsTrigger value="remarks" className="rounded-sm">Auditor Remarks</TabsTrigger>
        </TabsList>

        <TabsContent value="trail" className="mt-4">
          <div className="card-sharp overflow-hidden">
            <table className="w-full dense">
              <thead><tr><th>Timestamp</th><th>User</th><th>Action</th><th>Entity</th><th>Details</th><th></th></tr></thead>
              <tbody>
                {logs.length === 0 && <tr><td colSpan={6} className="text-center py-8 text-muted-foreground">No audit entries match your filters</td></tr>}
                {logs.map((l) => renderRow(l))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="danger" className="mt-4">
          <div className="card-sharp p-3 mb-3 border-l-4 border-l-red-500 bg-red-50/40 text-xs">
            <b>Danger Log</b> — every wipe, bulk-delete, org lifecycle change, and reversal. Filter above by user or date to build an incident timeline.
          </div>
          <div className="card-sharp overflow-hidden">
            <table className="w-full dense">
              <thead><tr><th>Timestamp</th><th>User</th><th>Action</th><th>Entity</th><th>Impact</th><th></th></tr></thead>
              <tbody>
                {dangerLogs.length === 0 && <tr><td colSpan={6} className="text-center py-8 text-muted-foreground">No dangerous actions recorded — clean bill.</td></tr>}
                {dangerLogs.map((l) => renderRow(l, "danger-"))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="gstin_access" className="mt-4">
          <div className="card-sharp overflow-hidden">
            <table className="w-full dense">
              <thead><tr><th>Timestamp</th><th>Changed By</th><th>Target User</th><th className="text-left">Before</th><th className="text-left">After</th></tr></thead>
              <tbody>
                {logs.filter((l) => l.entity === "user_gstin_access").length === 0 && (
                  <tr><td colSpan={5} className="text-center text-muted-foreground py-8">
                    No GSTIN-access changes logged yet.
                  </td></tr>
                )}
                {logs.filter((l) => l.entity === "user_gstin_access").map((l) => (
                  <tr key={l.id} onClick={() => setSelected(l)} className="cursor-pointer"
                        data-testid={`gstin-audit-row-${l.id}`}>
                    <td className="text-xs font-mono">{new Date(l.timestamp).toLocaleString("en-IN")}</td>
                    <td className="text-sm">{l.user_name}</td>
                    <td className="text-xs font-mono">{l.entity_id?.slice(0, 12) || "—"}</td>
                    <td className="text-xs">
                      {Array.isArray(l.before?.allowed_gstins)
                        ? l.before.allowed_gstins.join(", ") || "(none)"
                        : "(unrestricted)"}
                    </td>
                    <td className="text-xs">
                      {Array.isArray(l.after?.allowed_gstins)
                        ? l.after.allowed_gstins.join(", ") || "(none — locked out)"
                        : "(unrestricted)"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="remarks" className="mt-4">
          <div className="space-y-3">
            {remarks.length === 0 && <div className="text-center text-muted-foreground py-8 card-sharp">No remarks yet</div>}
            {remarks.map((r) => (
              <div key={r.id} className="card-sharp p-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <span className={`text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 border rounded-sm ${r.severity === "critical" ? "text-red-500 border-red-500/40" : r.severity === "warning" ? "text-warning border-warning/40" : "text-accent border-accent/40"}`}>{r.severity}</span>
                    <span className="text-xs text-muted-foreground">on {r.entity} · {r.entity_id?.slice(0, 8)}</span>
                  </div>
                  <div className="text-xs text-muted-foreground">{r.user_name} · {fmtDate(r.created_at)}</div>
                </div>
                <div className="text-sm">{r.remark}</div>
              </div>
            ))}
          </div>
        </TabsContent>
      </Tabs>

      <Sheet open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <SheetContent side="right" className="w-full sm:max-w-2xl rounded-none overflow-y-auto">
          {selected && (
            <>
              <SheetHeader><SheetTitle className="font-heading">Audit event detail</SheetTitle></SheetHeader>
              <div className="mt-4 space-y-3 text-sm">
                <div className="grid grid-cols-2 gap-3">
                  <div><span className="text-[10px] font-mono uppercase text-muted-foreground">Action</span><div className="font-mono mt-1">{selected.action}</div></div>
                  <div><span className="text-[10px] font-mono uppercase text-muted-foreground">Entity</span><div className="mt-1">{selected.entity}</div></div>
                  <div><span className="text-[10px] font-mono uppercase text-muted-foreground">User</span><div className="mt-1">{selected.user_name}{selected.after?.actor_role ? <span className="text-[10px] font-mono uppercase text-accent ml-2">{selected.after.actor_role}</span> : null}</div></div>
                  <div><span className="text-[10px] font-mono uppercase text-muted-foreground">Time</span><div className="mt-1 font-mono text-xs">{new Date(selected.timestamp).toLocaleString("en-IN")}</div></div>
                </div>
                <div className="border-t pt-3"><span className="text-[10px] font-mono uppercase text-muted-foreground">Entity ID</span><div className="mt-1 font-mono text-xs break-all">{selected.entity_id}</div></div>
                {selected.before && <div className="border-t pt-3"><div className="text-[10px] font-mono uppercase text-muted-foreground mb-1">Before</div><pre className="text-xs bg-muted p-2 rounded-sm overflow-x-auto max-h-72">{JSON.stringify(selected.before, null, 2)}</pre></div>}
                {selected.after && <div className="border-t pt-3"><div className="text-[10px] font-mono uppercase text-muted-foreground mb-1">After</div><pre className="text-xs bg-muted p-2 rounded-sm overflow-x-auto max-h-72">{JSON.stringify(selected.after, null, 2)}</pre></div>}
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="right" className="w-full sm:max-w-md rounded-none space-y-3">
          <SheetHeader><SheetTitle className="font-heading">New Auditor Remark</SheetTitle></SheetHeader>
          <div className="space-y-3">
            <div><Label>Entity</Label>
              <Select value={form.entity} onValueChange={(v) => setForm({ ...form, entity: v })}>
                <SelectTrigger className="rounded-sm mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="voucher">Voucher</SelectItem>
                  <SelectItem value="sales_invoice">Sales Invoice</SelectItem>
                  <SelectItem value="purchase_bill">Purchase Bill</SelectItem>
                  <SelectItem value="fixed_asset">Fixed Asset</SelectItem>
                  <SelectItem value="account">Account</SelectItem>
                  <SelectItem value="party">Party</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label>Entity ID / Reference</Label><Input value={form.entity_id} onChange={(e) => setForm({ ...form, entity_id: e.target.value })} className="rounded-sm mt-1" placeholder="Voucher #, Invoice # or ID" /></div>
            <div><Label>Severity</Label>
              <Select value={form.severity} onValueChange={(v) => setForm({ ...form, severity: v })}>
                <SelectTrigger className="rounded-sm mt-1"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="info">Info</SelectItem><SelectItem value="warning">Warning</SelectItem><SelectItem value="critical">Critical</SelectItem></SelectContent>
              </Select>
            </div>
            <div><Label>Remark</Label><Textarea value={form.remark} onChange={(e) => setForm({ ...form, remark: e.target.value })} className="rounded-sm mt-1" rows={5} data-testid="remark-text" /></div>
          </div>
          <Button onClick={addRemark} className="w-full rounded-sm bg-accent hover:bg-accent/90 text-white" data-testid="remark-save">Log Remark</Button>
        </SheetContent>
      </Sheet>
    </div>
  );
}
